import { Link, useNavigate, useLocation } from "react-router-dom";
import { Anchor, Compass, PlusCircle, Menu, X, LogIn, User, Crown } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import NotificationsBell from "./NotificationsBell";
import Avatar from "./Avatar";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const linkCls = (path) =>
    `text-sm font-semibold transition-colors ${
      loc.pathname === path ? "text-ocean" : "text-ocean/75 hover:text-ocean"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-blanc/90 backdrop-blur border-b border-brume">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5 group">
          <img
            src="/le-bon-matelot-logo.png"
            alt="Le Bon Matelot"
            className="w-10 h-10 rounded-full object-cover ring-2 ring-ocean/15 group-hover:ring-ocean/40 transition"
          />
          <span className="font-display text-xl font-bold text-ocean">Le Bon Matelot</span>
        </Link>

        {/* Primary nav — only 2 actions */}
        <nav className="hidden md:flex items-center gap-7">
          <Link to="/explorer" data-testid="nav-explorer" className={linkCls("/explorer")}>
            <span className="inline-flex items-center gap-1.5">
              <Compass className="w-4 h-4" /> Voir les sorties
            </span>
          </Link>
          <Link to="/publier" data-testid="nav-publish" className={linkCls("/publier")}>
            <Button
              size="sm"
              className="bg-ocean hover:bg-ocean/90 text-blanc font-bold rounded-full px-4 tactile shadow-matelot"
            >
              <PlusCircle className="w-4 h-4 mr-1.5" /> Proposer une sortie
            </Button>
          </Link>
          {/* Discreet secondary auth links */}
          {user ? (
            <div className="flex items-center gap-3">
              {user.is_founder && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  title="Espace fondateur"
                  className="inline-flex items-center gap-1.5 bg-jaune text-ocean text-[11px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-full hover:bg-jaune/80 tactile"
                >
                  <Crown className="w-3 h-3 fill-ocean" /> Admin
                </Link>
              )}
              <NotificationsBell />
              <Link
                to="/mon-espace"
                data-testid="nav-mon-espace"
                className="text-xs text-ocean/80 hover:text-ocean inline-flex items-center gap-1.5"
                title="Mon espace"
              >
                <Avatar name={user.name} picture={user.picture} size={28} />
                <span className="hidden lg:inline">{user.name?.split(" ")[0] || "Mon espace"}</span>
              </Link>
            </div>
          ) : (
            <Link
              to="/connexion"
              data-testid="nav-login"
              className="text-xs text-ocean/60 hover:text-ocean"
            >
              Se connecter
            </Link>
          )}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-ocean"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-brume bg-blanc px-4 py-3 flex flex-col gap-3">
          <Link to="/explorer" onClick={() => setOpen(false)} className="py-1">Voir les sorties</Link>
          <Link to="/publier" onClick={() => setOpen(false)} className="py-1 text-ocean font-bold">Proposer une sortie</Link>
          {user ? (
            <>
              {user.is_founder && (
                <Link to="/admin" onClick={() => setOpen(false)} className="py-1 text-ocean font-bold inline-flex items-center gap-1.5">
                  <Crown className="w-3.5 h-3.5 text-jaune fill-jaune" /> Espace fondateur
                </Link>
              )}
              <Link to="/mon-espace" onClick={() => setOpen(false)} className="py-1 text-ocean">Mon espace</Link>
              <button onClick={async () => { await logout(); setOpen(false); nav("/"); }}
                      className="text-left py-1 text-ocean/70 text-sm">Déconnexion</button>
            </>
          ) : (
            <Link to="/connexion" onClick={() => setOpen(false)} className="py-1 text-ocean/70 text-sm">Se connecter</Link>
          )}
        </div>
      )}
    </header>
  );
}
